import styled from '@emotion/styled';
import { type FC, type HTMLAttributes, type PropsWithChildren } from 'react';

import { resolveColor } from '../shared/colorUtils';
import {
  textFontWeightStyles,
  textSizeStyles,
  textUppercase,
  textWrapStyles,
} from './Text.styles';
import {
  type TFontWeight,
  type TTextAlign,
  type TTextSize,
  type TTextWrap,
} from './types';

export type TextProps = HTMLAttributes<HTMLElement> & {
  /**
   * Size variant of the text
   */
  size?: TTextSize;
  /**
   * DOM element name to render as
   */
  as?: string;
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Font weight of the text
   */
  fontWeight?: TFontWeight;
  /**
   * Whether to transform text to uppercase
   */
  uppercase?: boolean;
  /**
   * Text alignment
   */
  textAlign?: TTextAlign;
  /**
   * Text wrapping behavior
   */
  textWrap?: TTextWrap;
  /**
   * Color token or CSS color value
   */
  color?: string;
};

const StyledText = styled('p')<TextProps>`
  ${({ size = 'md' }) => textSizeStyles[size]}
  ${({ fontWeight = 'normal' }) => textFontWeightStyles[fontWeight]}
  ${({ uppercase }) => uppercase && textUppercase}
  ${({ textWrap = 'pretty' }) => textWrapStyles[textWrap]}
  ${({ textAlign }) => textAlign && `text-align: ${textAlign};`}
  ${({ color: colorProp }) => colorProp && `color: ${resolveColor(colorProp)};`}
`;

const Text: FC<PropsWithChildren<TextProps>> = ({
  as = 'p',
  size = 'md',
  children,
  className,
  fontWeight = 'normal',
  uppercase,
  textAlign,
  textWrap = 'pretty',
  color,
  ...props
}) => {
  const elementType = as as keyof JSX.IntrinsicElements;

  return (
    <StyledText
      as={elementType}
      size={size}
      className={className}
      fontWeight={fontWeight}
      uppercase={uppercase}
      textAlign={textAlign}
      textWrap={textWrap}
      color={color}
      {...props}
    >
      {children}
    </StyledText>
  );
};

export default Text;
